import { supabase } from '@/lib/supabase';
import { emitDailyLogSync, linkProjectFileToDailyLog, SUBCONTRACTOR_DAILY_LOG_SOURCE_MARKER } from '@/lib/dailyLogUtils';
import {
  fromSupabaseError,
  isValidDailyLogId,
  logShareQueryDebug,
  normalizeDailyLogId,
  PM_PHOTO_SUPPORTED_EXT,
  shareError,
  validatePhotoRecord,
  validatePmUploadFile,
  type DailyLogShareError,
  type PhotoShareValidation,
} from '@/lib/dailyLogShareDiagnostics';

type ShareQueryContext = {
  projectId?: string;
  logDate?: string;
};

function invalidDailyLogIdError(
  operation: DailyLogShareError['operation'],
  rawId: unknown,
  ctx?: Partial<DailyLogShareError>
): DailyLogShareError {
  return shareError(
    operation,
    'Daily Log ID could not be resolved. The submission was not sent.',
    {
      details: `Invalid dailyLogId: ${String(rawId)}`,
      dailyLogId: typeof rawId === 'string' ? rawId : undefined,
      ...ctx,
    }
  );
}

/**
 * Sole path for PATCH jphph_daily_logs by id in the client-share flow.
 * Guarantees .eq('id', <validated uuid>) — never id=eq.undefined.
 */
async function updateDailyLogById(
  operation: 'save_draft' | 'share_with_client',
  rawId: unknown,
  payload: Record<string, unknown>,
  ctx: ShareQueryContext & { logLabel?: string }
): Promise<{ ok: true; dailyLogId: string } | { ok: false; error: DailyLogShareError }> {
  const dailyLogId = normalizeDailyLogId(rawId);

  // eslint-disable-next-line no-console
  console.debug('[DailyLogShare] before update', {
    operation: ctx.logLabel || operation,
    dailyLogId,
    rawDailyLogId: typeof rawId === 'string' ? rawId : String(rawId),
    projectId: ctx.projectId ?? null,
    logDate: ctx.logDate ?? null,
  });

  if (!dailyLogId) {
    // eslint-disable-next-line no-console
    console.warn('[DailyLogShare] blocked update with invalid dailyLogId', {
      operation,
      rawDailyLogId: String(rawId),
      projectId: ctx.projectId ?? null,
      logDate: ctx.logDate ?? null,
    });
    return {
      ok: false,
      error: invalidDailyLogIdError(operation, rawId, {
        projectId: ctx.projectId,
        logDate: ctx.logDate,
      }),
    };
  }

  const { error } = await supabase
    .from('jphph_daily_logs')
    .update(payload)
    .eq('id', dailyLogId);

  if (error) {
    return {
      ok: false,
      error: fromSupabaseError(operation, error, { dailyLogId }),
    };
  }

  return { ok: true, dailyLogId };
}

export type ClientVersionStatus = 'draft' | 'shared' | 'archived';

export const PM_DAILY_LOG_PHOTO_NOTE = 'PM additional photo';

export function isPmAdditionalDailyLogPhoto(note: string | null | undefined): boolean {
  return note === PM_DAILY_LOG_PHOTO_NOTE;
}

const BASE_LOG_SELECT =
  'id, project_id, log_date, title, management_comment, internal_note, visibility, is_published_to_client, published_at, created_by, created_at, updated_at';

const ENRICHED_LOG_SELECT =
  `${BASE_LOG_SELECT}, pm_client_comment, client_version_status, client_shared_at, client_shared_by`;

export interface SubcontractorDailyLogRow {
  id: string;
  project_id: string;
  log_date: string;
  title: string | null;
  management_comment: string | null;
  internal_note: string | null;
  pm_client_comment?: string | null;
  client_version_status?: string | null;
  client_shared_at?: string | null;
  is_published_to_client: boolean;
}

export type DailyLogShareResult = { ok: true } | { ok: false; error: DailyLogShareError };

function isMissingColumnError(message: string): boolean {
  return /column|schema cache|does not exist/i.test(message);
}

export async function findSubcontractorDailyLog(
  projectId: string,
  logDate: string
): Promise<{ log: SubcontractorDailyLogRow | null; error?: DailyLogShareError }> {
  logShareQueryDebug('findSubcontractorDailyLog.enriched', { projectId, logDate });

  const baseQuery = () => {
    logShareQueryDebug('findSubcontractorDailyLog.base', { projectId, logDate });
    return supabase
      .from('jphph_daily_logs')
      .select(BASE_LOG_SELECT)
      .eq('project_id', projectId)
      .eq('log_date', logDate)
      .eq('internal_note', SUBCONTRACTOR_DAILY_LOG_SOURCE_MARKER)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
  };

  let { data, error } = await supabase
    .from('jphph_daily_logs')
    .select(ENRICHED_LOG_SELECT)
    .eq('project_id', projectId)
    .eq('log_date', logDate)
    .eq('internal_note', SUBCONTRACTOR_DAILY_LOG_SOURCE_MARKER)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error && isMissingColumnError(error.message)) {
    ({ data, error } = await baseQuery());
  }

  if (error) {
    return {
      log: null,
      error: fromSupabaseError('load_daily_log', error, { dailyLogId: undefined }),
    };
  }
  if (!data) return { log: null };
  const log = data as SubcontractorDailyLogRow;
  if (!isValidDailyLogId(log.id)) {
    return {
      log: null,
      error: invalidDailyLogIdError('load_daily_log', log.id, { projectId, logDate }),
    };
  }
  return { log };
}

/** Pick the most common daily_log_id linked on project file rows. */
export function pickDailyLogIdFromPhotos(
  photos: Array<{ daily_log_id?: string | null }>
): string | null {
  const counts = new Map<string, number>();
  for (const photo of photos) {
    const id = photo.daily_log_id;
    if (isValidDailyLogId(id)) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  let bestId: string | null = null;
  let bestCount = 0;
  for (const [id, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      bestId = id;
    }
  }
  return bestId;
}

async function fetchDailyLogRowById(
  dailyLogId: string,
  projectId: string,
  logDate?: string
): Promise<{ log: SubcontractorDailyLogRow | null; error?: DailyLogShareError }> {
  const normalizedId = normalizeDailyLogId(dailyLogId);
  if (!normalizedId) {
    return {
      log: null,
      error: invalidDailyLogIdError('load_daily_log', dailyLogId, {
        dailyLogId: typeof dailyLogId === 'string' ? dailyLogId : undefined,
        projectId,
        logDate,
      }),
    };
  }

  logShareQueryDebug('fetchDailyLogRowById.enriched', {
    dailyLogId: normalizedId,
    projectId,
    logDate,
  });

  let { data, error } = await supabase
    .from('jphph_daily_logs')
    .select(ENRICHED_LOG_SELECT)
    .eq('id', normalizedId)
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .maybeSingle();

  if (error && isMissingColumnError(error.message)) {
    logShareQueryDebug('fetchDailyLogRowById.base', {
      dailyLogId: normalizedId,
      projectId,
      logDate,
    });
    ({ data, error } = await supabase
      .from('jphph_daily_logs')
      .select(BASE_LOG_SELECT)
      .eq('id', normalizedId)
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .maybeSingle());
  }

  if (error) {
    return {
      log: null,
      error: fromSupabaseError('load_daily_log', error, { dailyLogId: normalizedId }),
    };
  }
  if (!data) return { log: null };
  const log = data as SubcontractorDailyLogRow;
  if (!isValidDailyLogId(log.id)) {
    return {
      log: null,
      error: invalidDailyLogIdError('load_daily_log', log.id, {
        dailyLogId: normalizedId,
        projectId,
        logDate,
      }),
    };
  }
  return { log };
}

/**
 * Resolve daily log for client share.
 * Order: photo.daily_log_id → subcontractor marker row → project_id+log_date fallback.
 * Never treats a photo-group / file id as the daily log row id.
 */
export async function resolveDailyLogIdForClientShare(
  projectId: string,
  logDate: string,
  photos: Array<{ daily_log_id?: string | null }> = [],
  existingDailyLogId?: string | null
): Promise<{ dailyLogId: string | null; log: SubcontractorDailyLogRow | null; error?: DailyLogShareError }> {
  logShareQueryDebug('resolveDailyLogIdForClientShare.start', {
    dailyLogId: existingDailyLogId ?? null,
    projectId,
    logDate,
  });

  const existingId = normalizeDailyLogId(existingDailyLogId);
  if (existingId) {
    const existing = await fetchDailyLogRowById(existingId, projectId, logDate);
    if (existing.log?.id && isValidDailyLogId(existing.log.id)) {
      return { dailyLogId: existing.log.id, log: existing.log };
    }
  }

  const fromPhotos = pickDailyLogIdFromPhotos(photos);
  if (fromPhotos) {
    const linked = await fetchDailyLogRowById(fromPhotos, projectId, logDate);
    if (linked.error && !linked.log) {
      return { dailyLogId: null, log: null, error: linked.error };
    }
    if (linked.log?.id && isValidDailyLogId(linked.log.id)) {
      return { dailyLogId: linked.log.id, log: linked.log };
    }
  }

  const markerResult = await findSubcontractorDailyLog(projectId, logDate);
  if (markerResult.log?.id && isValidDailyLogId(markerResult.log.id)) {
    return { dailyLogId: markerResult.log.id, log: markerResult.log };
  }

  logShareQueryDebug('resolveDailyLogIdForClientShare.fallback.enriched', {
    dailyLogId: fromPhotos,
    projectId,
    logDate,
  });

  let { data: fallback, error: fallbackErr } = await supabase
    .from('jphph_daily_logs')
    .select(ENRICHED_LOG_SELECT)
    .eq('project_id', projectId)
    .eq('log_date', logDate)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (fallbackErr && isMissingColumnError(fallbackErr.message)) {
    logShareQueryDebug('resolveDailyLogIdForClientShare.fallback.base', {
      dailyLogId: fromPhotos,
      projectId,
      logDate,
    });
    ({ data: fallback, error: fallbackErr } = await supabase
      .from('jphph_daily_logs')
      .select(BASE_LOG_SELECT)
      .eq('project_id', projectId)
      .eq('log_date', logDate)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle());
  }

  if (fallbackErr) {
    return {
      dailyLogId: null,
      log: null,
      error: fromSupabaseError('load_daily_log', fallbackErr, {
        dailyLogId: fromPhotos ?? undefined,
      }),
    };
  }

  if (fallback?.id && isValidDailyLogId(fallback.id)) {
    return {
      dailyLogId: fallback.id,
      log: fallback as SubcontractorDailyLogRow,
    };
  }

  if (fallback?.id) {
    return {
      dailyLogId: null,
      log: null,
      error: invalidDailyLogIdError('load_daily_log', fallback.id, {
        dailyLogId: fromPhotos ?? undefined,
        projectId,
        logDate,
      }),
    };
  }

  if (markerResult.error) {
    return { dailyLogId: null, log: null, error: markerResult.error };
  }

  return {
    dailyLogId: null,
    log: null,
    error: shareError(
      'load_daily_log',
      'Daily Log ID could not be resolved. The submission was not sent.',
      { dailyLogId: fromPhotos ?? undefined, projectId, logDate }
    ),
  };
}

export interface PmDailyLogUploadRow {
  id: string;
  url: string;
  name: string;
  valid: boolean;
  invalidReason?: string;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

export async function loadPmDailyLogUploads(
  dailyLogId: string,
  context?: ShareQueryContext
): Promise<{ uploads: PmDailyLogUploadRow[]; error?: DailyLogShareError }> {
  if (!isValidDailyLogId(dailyLogId)) {
    return {
      uploads: [],
      error: invalidDailyLogIdError('load_photos', dailyLogId, {
        dailyLogId: typeof dailyLogId === 'string' ? dailyLogId : undefined,
        projectId: context?.projectId,
        logDate: context?.logDate,
      }),
    };
  }

  logShareQueryDebug('loadPmDailyLogUploads', {
    dailyLogId,
    projectId: context?.projectId,
    logDate: context?.logDate,
  });

  const { data, error } = await supabase
    .from('jphph_project_files')
    .select('id, file_url, file_name, note, uploaded_context, is_deleted, storage_path, storage_bucket')
    .eq('daily_log_id', dailyLogId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });

  if (error) {
    return {
      uploads: [],
      error: fromSupabaseError('load_photos', error, { dailyLogId }),
    };
  }

  const uploads = (data || [])
    .filter((row) => isPmAdditionalDailyLogPhoto((row as { note?: string | null }).note))
    .map((row) => {
      const rec = row as {
        id: string;
        file_url: string | null;
        file_name: string;
        is_deleted?: boolean;
        storage_path?: string | null;
        storage_bucket?: string | null;
      };
      const validation = validatePhotoRecord(rec);
      return {
        id: rec.id,
        url: rec.file_url || '',
        name: rec.file_name,
        valid: validation.valid,
        invalidReason: validation.reason,
        storage_bucket: rec.storage_bucket ?? null,
        storage_path: rec.storage_path ?? null,
      };
    });

  return { uploads };
}

export async function validateSelectedPhotosForShare(
  photoIds: string[],
  context?: ShareQueryContext & { dailyLogId?: string }
): Promise<{ valid: PhotoShareValidation[]; invalid: PhotoShareValidation[] }> {
  const uniqueIds = Array.from(new Set(photoIds.filter(isValidDailyLogId)));
  if (uniqueIds.length === 0) {
    return { valid: [], invalid: [] };
  }

  logShareQueryDebug('validateSelectedPhotosForShare', {
    dailyLogId: context?.dailyLogId ?? null,
    projectId: context?.projectId ?? null,
    logDate: context?.logDate ?? null,
  });

  const { data, error } = await supabase
    .from('jphph_project_files')
    .select('id, file_url, file_name, is_deleted, storage_path')
    .in('id', uniqueIds);

  if (error) {
    throw fromSupabaseError('validate_selected_photos', error);
  }

  const rowMap = new Map(
    (data || []).map((row) => {
      const rec = row as {
        id: string;
        file_url: string | null;
        file_name: string | null;
        is_deleted?: boolean;
        storage_path?: string | null;
      };
      return [rec.id, rec];
    })
  );

  const valid: PhotoShareValidation[] = [];
  const invalid: PhotoShareValidation[] = [];

  for (const id of uniqueIds) {
    const row = rowMap.get(id);
    if (!row) {
      invalid.push({
        id,
        valid: false,
        reason: 'Selected photo no longer exists in project files.',
        fileName: undefined,
      });
      continue;
    }
    const validation = validatePhotoRecord(row);
    if (validation.valid) valid.push(validation);
    else invalid.push(validation);
  }

  return { valid, invalid };
}

async function safeUpdateReviewStatus(
  photoIds: string[],
  status: 'approved_for_client' | 'internal_only' | 'rejected',
  dailyLogId: string,
  context?: ShareQueryContext
): Promise<DailyLogShareResult> {
  const safeIds = photoIds.filter(isValidDailyLogId);
  if (safeIds.length === 0) return { ok: true };

  const isClientVisible = status === 'approved_for_client';
  logShareQueryDebug('safeUpdateReviewStatus', {
    dailyLogId,
    projectId: context?.projectId,
    logDate: context?.logDate,
  });

  const { error } = await supabase
    .from('jphph_project_files')
    .update({
      review_status: status,
      is_client_visible: isClientVisible,
    })
    .in('id', safeIds);

  if (error) {
    return {
      ok: false,
      error: fromSupabaseError('update_selected_photos', error, {
        dailyLogId,
        photoId: safeIds[0],
      }),
    };
  }
  return { ok: true };
}

export async function saveDailyLogClientDraft(params: {
  dailyLogId: string;
  projectId: string;
  pmClientComment: string;
  pmUserId: string;
  logDate?: string;
}): Promise<DailyLogShareResult> {
  const dailyLogId = normalizeDailyLogId(params.dailyLogId);
  if (!dailyLogId) {
    return {
      ok: false,
      error: invalidDailyLogIdError('save_draft', params.dailyLogId, {
        projectId: params.projectId,
        logDate: params.logDate,
      }),
    };
  }

  const payload = {
    pm_client_comment: params.pmClientComment.trim() || null,
    client_version_status: 'draft' as const,
    updated_at: new Date().toISOString(),
  };

  const result = await updateDailyLogById('save_draft', dailyLogId, payload, {
    projectId: params.projectId,
    logDate: params.logDate,
    logLabel: 'saveDraft',
  });

  if (!result.ok) {
    if (result.error.message && isMissingColumnError(result.error.message)) {
      return {
        ok: false,
        error: shareError(
          'save_draft',
          'PM client comment fields are not available yet. Run the daily log client enrichment SQL migration.',
          { dailyLogId }
        ),
      };
    }
    return result;
  }

  emitDailyLogSync(params.projectId);
  return { ok: true };
}

export async function shareDailyLogWithClient(params: {
  dailyLogId: string;
  projectId: string;
  logDate: string;
  pmClientComment: string;
  pmUserId: string;
  selectedPhotoIds: string[];
  allSubcontractorPhotoIds: string[];
  pmUploadedFileIds: string[];
}): Promise<DailyLogShareResult> {
  const dailyLogId = normalizeDailyLogId(params.dailyLogId);
  if (!dailyLogId) {
    return {
      ok: false,
      error: invalidDailyLogIdError('share_with_client', params.dailyLogId, {
        projectId: params.projectId,
        logDate: params.logDate,
      }),
    };
  }

  const shareContext: ShareQueryContext = {
    projectId: params.projectId,
    logDate: params.logDate,
  };

  const selectedIds = Array.from(new Set(params.selectedPhotoIds.filter(isValidDailyLogId)));

  const draftResult = await saveDailyLogClientDraft({
    dailyLogId,
    projectId: params.projectId,
    pmClientComment: params.pmClientComment,
    pmUserId: params.pmUserId,
    logDate: params.logDate,
  });
  if (!draftResult.ok) {
    return draftResult;
  }

  let validation: { valid: PhotoShareValidation[]; invalid: PhotoShareValidation[] };
  try {
    validation = await validateSelectedPhotosForShare(selectedIds, {
      dailyLogId,
      projectId: params.projectId,
      logDate: params.logDate,
    });
  } catch (err) {
    return {
      ok: false,
      error:
        err && typeof err === 'object' && 'operation' in err
          ? (err as DailyLogShareError)
          : shareError('validate_selected_photos', String(err), { dailyLogId }),
    };
  }

  if (validation.invalid.length > 0) {
    const first = validation.invalid[0];
    return {
      ok: false,
      error: shareError(
        'validate_selected_photos',
        first.reason || 'A selected photo cannot be shared.',
        {
          dailyLogId,
          photoId: first.id,
          fileName: first.fileName,
        }
      ),
    };
  }

  const selectedSet = new Set(selectedIds);
  const approvedIds = selectedIds;
  const internalIds = params.allSubcontractorPhotoIds.filter(
    (id) => isValidDailyLogId(id) && !selectedSet.has(id)
  );
  const unselectedPmIds = params.pmUploadedFileIds.filter(
    (id) => isValidDailyLogId(id) && !selectedSet.has(id)
  );

  const reviewApproved = await safeUpdateReviewStatus(
    approvedIds,
    'approved_for_client',
    dailyLogId,
    shareContext
  );
  if (!reviewApproved.ok) return reviewApproved;

  const reviewInternal = await safeUpdateReviewStatus(
    internalIds,
    'internal_only',
    dailyLogId,
    shareContext
  );
  if (!reviewInternal.ok) return reviewInternal;

  const reviewPmInternal = await safeUpdateReviewStatus(
    unselectedPmIds,
    'internal_only',
    dailyLogId,
    shareContext
  );
  if (!reviewPmInternal.ok) return reviewPmInternal;

  if (approvedIds.length > 0) {
    logShareQueryDebug('shareDailyLogWithClient.fetchApprovedFiles', {
      dailyLogId,
      projectId: params.projectId,
      logDate: params.logDate,
    });

    const { data: fileRows, error: fileFetchErr } = await supabase
      .from('jphph_project_files')
      .select('id, file_url')
      .in('id', approvedIds);

    if (fileFetchErr) {
      return {
        ok: false,
        error: fromSupabaseError('link_pm_photo', fileFetchErr, { dailyLogId }),
      };
    }

    for (const row of fileRows || []) {
      const fileId = (row as { id: string }).id;
      const fileUrl = (row as { file_url: string | null }).file_url;
      if (!fileUrl?.trim()) {
        return {
          ok: false,
          error: shareError(
            'link_pm_photo',
            'A selected photo has an incomplete file record (missing URL).',
            { dailyLogId, photoId: fileId }
          ),
        };
      }

      const linkResult = await linkProjectFileToDailyLog({
        dailyLogId,
        projectId: params.projectId,
        fileId,
        photoUrl: fileUrl,
        uploadedBy: params.pmUserId,
      });

      if (linkResult.error && !/duplicate|unique|already exists/i.test(linkResult.error)) {
        return {
          ok: false,
          error: shareError('link_pm_photo', linkResult.error, {
            dailyLogId,
            photoId: fileId,
          }),
        };
      }
    }
  }

  const now = new Date().toISOString();
  const updatePayload: Record<string, unknown> = {
    pm_client_comment: params.pmClientComment.trim() || null,
    client_version_status: 'shared',
    client_shared_at: now,
    client_shared_by: params.pmUserId,
    visibility: 'client',
    is_published_to_client: true,
    published_at: now,
    updated_at: now,
  };

  const shareUpdate = await updateDailyLogById('share_with_client', dailyLogId, updatePayload, {
    projectId: params.projectId,
    logDate: params.logDate,
    logLabel: 'shareWithClient',
  });

  if (!shareUpdate.ok) {
    if (shareUpdate.error.message && isMissingColumnError(shareUpdate.error.message)) {
      const fallbackPayload = {
        visibility: 'client',
        is_published_to_client: true,
        published_at: now,
        updated_at: now,
      };
      const fallbackUpdate = await updateDailyLogById(
        'share_with_client',
        dailyLogId,
        fallbackPayload,
        {
          projectId: params.projectId,
          logDate: params.logDate,
          logLabel: 'shareWithClient.base',
        }
      );
      if (!fallbackUpdate.ok) return fallbackUpdate;
    } else {
      return shareUpdate;
    }
  }

  emitDailyLogSync(params.projectId);
  return { ok: true };
}

export async function deletePmDailyLogPhoto(params: {
  fileId: string;
  dailyLogId: string;
  projectId: string;
  logDate?: string;
}): Promise<DailyLogShareResult> {
  if (!isValidDailyLogId(params.dailyLogId)) {
    return {
      ok: false,
      error: invalidDailyLogIdError('delete_pm_photo', params.dailyLogId, {
        dailyLogId: typeof params.dailyLogId === 'string' ? params.dailyLogId : undefined,
        projectId: params.projectId,
        logDate: params.logDate,
        photoId: params.fileId,
      }),
    };
  }

  if (!isValidDailyLogId(params.fileId)) {
    return {
      ok: false,
      error: invalidDailyLogIdError('delete_pm_photo', params.fileId, {
        dailyLogId: params.dailyLogId,
        projectId: params.projectId,
        logDate: params.logDate,
        photoId: typeof params.fileId === 'string' ? params.fileId : undefined,
      }),
    };
  }

  logShareQueryDebug('deletePmDailyLogPhoto.fetch', {
    dailyLogId: params.dailyLogId,
    projectId: params.projectId,
    logDate: params.logDate,
  });

  const { data: fileRow, error: fetchErr } = await supabase
    .from('jphph_project_files')
    .select('id, note')
    .eq('id', params.fileId)
    .eq('daily_log_id', params.dailyLogId)
    .maybeSingle();

  if (fetchErr) {
    return {
      ok: false,
      error: fromSupabaseError('delete_pm_photo', fetchErr, {
        dailyLogId: params.dailyLogId,
        photoId: params.fileId,
      }),
    };
  }
  if (!fileRow || !isPmAdditionalDailyLogPhoto((fileRow as { note?: string | null }).note)) {
    return {
      ok: false,
      error: shareError('delete_pm_photo', 'Only PM-added photos can be removed from client prep.', {
        dailyLogId: params.dailyLogId,
        photoId: params.fileId,
      }),
    };
  }

  logShareQueryDebug('deletePmDailyLogPhoto.softDelete', {
    dailyLogId: params.dailyLogId,
    projectId: params.projectId,
    logDate: params.logDate,
  });

  const { error: fileErr } = await supabase
    .from('jphph_project_files')
    .update({ is_deleted: true, is_visible: false })
    .eq('id', params.fileId);

  if (fileErr) {
    return {
      ok: false,
      error: fromSupabaseError('delete_pm_photo', fileErr, {
        dailyLogId: params.dailyLogId,
        photoId: params.fileId,
      }),
    };
  }

  logShareQueryDebug('deletePmDailyLogPhoto.unlinkDailyLogPhotos', {
    dailyLogId: params.dailyLogId,
    projectId: params.projectId,
    logDate: params.logDate,
  });

  await supabase.from('jphph_daily_log_photos').delete().eq('source_photo_id', params.fileId);

  logShareQueryDebug('deletePmDailyLogPhoto.unlinkDailyLogFiles', {
    dailyLogId: params.dailyLogId,
    projectId: params.projectId,
    logDate: params.logDate,
  });

  await supabase
    .from('jphph_daily_log_files')
    .delete()
    .eq('daily_log_id', params.dailyLogId)
    .eq('file_id', params.fileId);

  emitDailyLogSync(params.projectId);
  return { ok: true };
}

export async function uploadPmDailyLogPhoto(params: {
  file: File;
  projectId: string;
  logDate: string;
  dailyLogId: string;
  userId: string;
  caption?: string;
}): Promise<{ fileId: string | null; fileUrl: string | null; error?: DailyLogShareError }> {
  if (!isValidDailyLogId(params.dailyLogId)) {
    return {
      fileId: null,
      fileUrl: null,
      error: invalidDailyLogIdError('upload_pm_photo', params.dailyLogId, {
        dailyLogId: typeof params.dailyLogId === 'string' ? params.dailyLogId : undefined,
        projectId: params.projectId,
        logDate: params.logDate,
        fileName: params.file.name,
      }),
    };
  }

  const fileCheck = validatePmUploadFile(params.file);
  if (!fileCheck.ok) {
    return { fileId: null, fileUrl: null, error: fileCheck.error };
  }

  const rawExt = params.file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const ext = PM_PHOTO_SUPPORTED_EXT.has(rawExt) ? rawExt : 'jpg';
  const safeName = params.file.name.replace(/[^a-zA-Z0-9._-]/g, '_') || `photo_${Date.now()}.${ext}`;
  const ts = Date.now();
  const primaryPath = `daily-log-photos/${params.projectId}/${params.logDate}/${ts}_${safeName}`;
  const fallbackPath = `project-photos/${params.projectId}/${ts}_${safeName}`;

  let publicUrl: string | null = null;
  let resolvedBucket = '';
  let resolvedPath = '';
  let storageErrorMessage = '';

  const { error: primaryStorageError } = await supabase.storage
    .from('daily-log-photos')
    .upload(primaryPath, params.file, { upsert: false });

  if (!primaryStorageError) {
    const { data: urlData } = supabase.storage.from('daily-log-photos').getPublicUrl(primaryPath);
    publicUrl = urlData?.publicUrl ?? null;
    resolvedBucket = 'daily-log-photos';
    resolvedPath = primaryPath;
  } else {
    storageErrorMessage = primaryStorageError.message;
    const { error: fallbackStorageError } = await supabase.storage
      .from('project-photos')
      .upload(fallbackPath, params.file, { upsert: false });

    if (fallbackStorageError) {
      return {
        fileId: null,
        fileUrl: null,
        error: shareError(
          'upload_pm_photo',
          `Storage upload failed: ${primaryStorageError.message}`,
          {
            dailyLogId: params.dailyLogId,
            fileName: params.file.name,
            details: fallbackStorageError.message,
          }
        ),
      };
    }

    const { data: urlData } = supabase.storage.from('project-photos').getPublicUrl(fallbackPath);
    publicUrl = urlData?.publicUrl ?? null;
    resolvedBucket = 'project-photos';
    resolvedPath = fallbackPath;
  }

  if (!publicUrl) {
    return {
      fileId: null,
      fileUrl: null,
      error: shareError(
        'upload_pm_photo',
        'Storage upload succeeded but no public URL was returned.',
        {
          dailyLogId: params.dailyLogId,
          fileName: params.file.name,
          details: storageErrorMessage || undefined,
        }
      ),
    };
  }

  const filePayload = {
    project_id: params.projectId,
    file_name: params.file.name,
    file_url: publicUrl,
    storage_bucket: resolvedBucket,
    storage_path: resolvedPath,
    file_size: String(params.file.size),
    file_type: ext,
    log_date: params.logDate,
    uploaded_context: 'daily_log',
    review_status: 'pending_management_review',
    is_client_visible: false,
    is_visible: true,
    is_deleted: false,
    caption: params.caption?.trim() || null,
    uploaded_by: params.userId,
    daily_log_id: params.dailyLogId,
    note: PM_DAILY_LOG_PHOTO_NOTE,
  };

  logShareQueryDebug('uploadPmDailyLogPhoto.insert', {
    dailyLogId: params.dailyLogId,
    projectId: params.projectId,
    logDate: params.logDate,
  });

  const { data, error } = await supabase
    .from('jphph_project_files')
    .insert(filePayload)
    .select('id, file_url')
    .maybeSingle();

  if (error || !data) {
    return {
      fileId: null,
      fileUrl: null,
      error: fromSupabaseError('upload_pm_photo', error || { message: 'Failed to save PM photo record' }, {
        dailyLogId: params.dailyLogId,
        fileName: params.file.name,
      }),
    };
  }

  const fileId = (data as { id: string }).id;
  const fileUrl = (data as { file_url: string }).file_url;

  const linkResult = await linkProjectFileToDailyLog({
    dailyLogId: params.dailyLogId,
    projectId: params.projectId,
    fileId,
    photoUrl: fileUrl,
    caption: params.caption || null,
    uploadedBy: params.userId,
  });

  if (linkResult.error && !/duplicate|unique|already exists/i.test(linkResult.error)) {
    return {
      fileId: null,
      fileUrl: null,
      error: shareError('link_pm_photo', `Photo uploaded but could not attach to daily log: ${linkResult.error}`, {
        dailyLogId: params.dailyLogId,
        photoId: fileId,
        fileName: params.file.name,
      }),
    };
  }

  emitDailyLogSync(params.projectId);
  return { fileId, fileUrl };
}

export { ENRICHED_LOG_SELECT, BASE_LOG_SELECT, isValidDailyLogId, normalizeDailyLogId };
